"use client";

/**
 * SqlEditorMonaco — lazy-loaded Monaco editor with SQL syntax highlighting.
 *
 * SOTA editor primitive (May 2026) — replaces V1's plain `<textarea>` with
 * a real editor (line numbers, syntax colourisation, multi-line caret).
 * Loaded lazily so the Monaco runtime + worker bundles only land on the
 * routes that mount this component (dashboard tab, inline SQL editor in
 * structured-data panels).
 *
 * Why a wrapper:
 *   - Provides our cream + ink token theme so Monaco doesn't ship its
 *     default dark theme on first paint.
 *   - Configures fixed-height + scrolling so it slots inside the card body
 *     without bleeding into the parent layout.
 *   - Wraps the @monaco-editor/react default export in `React.lazy()` so
 *     consumers can opt-into SSR-skip via React Suspense.
 *
 * Consumed by `<ChartSqlEditor>` (PR-4) and earmarked for the future
 * inline-SQL-edit affordance in `<EnterpriseDataPanel>` (PR-A3 follow-up).
 */

import * as React from "react";
import { cn } from "../lib/cn";

const MonacoEditorReact = React.lazy(async () => {
  const mod = await import("@monaco-editor/react");
  return { default: mod.default };
});

export interface SqlEditorMonacoProps {
  /** Controlled SQL string. */
  readonly value: string;
  /** Fires on every keystroke (Monaco debounces internally at ~50ms). */
  readonly onChange: (next: string) => void;
  /** Read-only display mode. Defaults to `false`. */
  readonly readOnly?: boolean;
  /** Editor height in CSS units. Defaults to `"160px"`. */
  readonly height?: string;
  /** Optional aria-label on the wrapping region. */
  readonly ariaLabel?: string;
  /** Extra classes appended to the root container. */
  readonly className?: string;
}

/**
 * Monaco theme tokens aligned with the Ando cream + ink palette.
 * Defined inline (not via `defineTheme`) so we can register it lazily
 * when the editor mounts, avoiding global side-effects at import time.
 */
const ANDO_MONACO_THEME = {
  base: "vs" as const,
  inherit: true,
  rules: [
    { token: "comment", foreground: "8A8884" /* ink-3 */, fontStyle: "italic" },
    { token: "keyword", foreground: "C75A12" /* ando-orange darker */, fontStyle: "bold" },
    { token: "operator", foreground: "56544F" /* ink-2 */ },
    { token: "string", foreground: "10B981" /* emerald */ },
    { token: "number", foreground: "3B82F6" /* blue */ },
    { token: "identifier", foreground: "1F1D1A" /* ink-1 */ },
  ],
  colors: {
    "editor.background": "#F8F4ED" /* cream */,
    "editor.foreground": "#1F1D1A" /* ink-1 */,
    "editor.lineHighlightBackground": "#EFE9DD" /* cream-deep */,
    "editorLineNumber.foreground": "#BDBBB6" /* ink-4 */,
    "editorLineNumber.activeForeground": "#56544F" /* ink-2 */,
    "editorCursor.foreground": "#FF7A1A" /* ando-orange */,
    "editor.selectionBackground": "#FF7A1A33",
    "editor.inactiveSelectionBackground": "#FF7A1A1A",
  },
};

/**
 * Internal helper — runs once per editor mount to register our theme.
 * Idempotent: Monaco silently overwrites previously-registered themes
 * of the same name.
 */
function registerAndoTheme(monaco: typeof import("monaco-editor") | undefined) {
  if (!monaco) return;
  monaco.editor.defineTheme("ando-cream", ANDO_MONACO_THEME);
}

export const SqlEditorMonaco: React.FC<SqlEditorMonacoProps> = ({
  value,
  onChange,
  readOnly = false,
  height = "160px",
  ariaLabel = "Editor SQL",
  className,
}) => {
  // Editing must remain available while the optional Monaco runtime loads.
  const fallback = (
    <textarea
      aria-label={ariaLabel}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      readOnly={readOnly}
      spellCheck={false}
      style={{ height }}
      className="block w-full resize-y bg-cream p-3 font-mono text-[12px] text-ink-1 outline-none"
    />
  );
  return (
    <section
      className={cn(
        "min-w-0 w-full max-w-full overflow-hidden rounded-md border border-hairline bg-cream-deep",
        "focus-within:ring-2 focus-within:ring-ando-orange/30",
        className,
      )}
      aria-label={ariaLabel}
    >
      <React.Suspense fallback={fallback}>
        <MonacoEditorReact
          height={height}
          language="sql"
          value={value}
          onChange={(next) => onChange(next ?? "")}
          theme="ando-cream"
          beforeMount={registerAndoTheme}
          options={{
            readOnly,
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            fontSize: 12,
            fontFamily: '"JetBrains Mono Variable", ui-monospace, SFMono-Regular, monospace',
            lineNumbers: "on",
            renderLineHighlight: "line",
            scrollbar: {
              vertical: "auto",
              horizontal: "auto",
              verticalScrollbarSize: 8,
              horizontalScrollbarSize: 8,
            },
            padding: { top: 8, bottom: 8 },
            wordWrap: "on",
            tabSize: 2,
            automaticLayout: true,
            contextmenu: false,
          }}
          loading={fallback}
        />
      </React.Suspense>
    </section>
  );
};

SqlEditorMonaco.displayName = "SqlEditorMonaco";
